/* 
    Service API Base URL
    
    Auto-detects environment based on hostname:
    - localhost/127.0.0.1 → Local API (http://localhost:3000)
    - Production domains → Firebase Functions API
*/
const isLocalDevelopment = typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || 
     window.location.hostname === '127.0.0.1' ||
     //cater for local subdomains
     window.location.hostname.includes('.localhost') ||
     window.location.hostname.endsWith('.localhost'));

export const baseUrl = isLocalDevelopment 
    ? 'http://localhost:3000'
    : 'https://europe-west1-rnw-template.cloudfunctions.net/api';


// App prefix
export const prefix = "rnwtemplate://";

// App - Default fallback values (used when store config fails to load)
export const CompanyName = "Cosmo Cart";
export const AppHeaderName = "COSMO";
export const FooterAppName = "Cosmo";
export const AppLogo = "icon-black-bg.png";
export const CURRENCY = "R";

// Re-export theme configuration for backwards compatibility
export {
    primaryColor,
    stateErrorColor,
    stateSuccessColor,
    stateWarningColor,
    stateInfoColor,
    stateDisabledColor,
    themeColors,
    ActivityTypeColors,
    PortalUIConfig,
    darkBackgroundSecondary,
    lightBackgroundSecondary
} from './theme';
export type { ActivityTypeColorKey, ActivityTypeColorValue } from './theme';

// Root paths
export const RootPaths = {
    Shop: 'shop',
    Product: 'product',
    Orders: 'orders',
    Contact_Us: 'contactus',
    Cart: 'cart',
    Portal: {
        path: 'portal',
        screens: {
            Dashboard: 'dashboard',
            Products: 'products', 
            Orders: 'orders',
            Users: 'users',
            Roles: 'roles',
            Customers: 'customers',
        }
    },
    NotFound: '*',
    // Custom screens - add new custom screen paths here
    // The path here is what appears in the URL (e.g., /home)
    CapeRootsHome: 'home',
    CapeRootsRestaurants: 'restaurants',
}

export const VAT_PERCENTAGE = 15;

// Default theme color (fallback)
export const DEFAULT_PRIMARY_COLOR = '#800080';

// Product Image Upload Configuration
export const MAX_PRODUCT_IMAGES = 6;
export const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
export const MAX_IMAGE_SIZE_MB = 2;

// API Endpoints
export const ApiEndpoints = {
    Auth: {
        SignIn: `${baseUrl}/auth/signin`,
        SignUp: `${baseUrl}/auth/signup`,
        SignOut: `${baseUrl}/auth/signout`,
        Refresh: `${baseUrl}/auth/refresh`,
    },
    Orders: {
        GetAll: `${baseUrl}/orders`,
        GetById: (id: string) => `${baseUrl}/orders/${id}`,
        Create: `${baseUrl}/orders`,
        Update: `${baseUrl}/orders`,
        MyOrders: `${baseUrl}/orders/my-orders`,
        Cancel: (id: string) => `${baseUrl}/orders/${id}/cancel`,
    },
    Refunds: {
        Request: `${baseUrl}/refunds/request`,
        MyRefunds: `${baseUrl}/refunds/my-refunds`,
        List: `${baseUrl}/refunds`,
        Approve: (id: string) => `${baseUrl}/refunds/${id}/approve`,
        Reject: (id: string) => `${baseUrl}/refunds/${id}/reject`,
    },
    Products: {
        GetAll: `${baseUrl}/products`,
        GetById: (id: string) => `${baseUrl}/products/${id}`,
        Create: `${baseUrl}/products`,
        Update: `${baseUrl}/products`,
        Delete: (id: string, code: string) => `${baseUrl}/products/${id}/${code}`,
        DeleteDummy: `${baseUrl}/products/dummy/all`,
    },
    Admin: {
        SignIn: `${baseUrl}/admin/signin`,
        SignUp: `${baseUrl}/admin/signup`,
    },
    Dashboard: {
        GetOrderStatuses: `${baseUrl}/dashboard/order-statuses`,
        GetAvgOrderValue: (startDate: string, endDate: string) => 
            `${baseUrl}/dashboard/avg-order-value?start_date=${startDate}&end_date=${endDate}`,
        GetTotalOrders: (startDate: string, endDate: string) => 
            `${baseUrl}/dashboard/total-orders?start_date=${startDate}&end_date=${endDate}`,
        GetTotalSales: (startDate: string, endDate: string) => 
            `${baseUrl}/dashboard/total-sales?start_date=${startDate}&end_date=${endDate}`,
        GetRevenueData: `${baseUrl}/dashboard/revenue-data`,
    },
    RoleManagement: {
        GetAllRoles: `${baseUrl}/admin/roles`,
        CreateRole: `${baseUrl}/admin/roles`,
        UpdateRole: (id: string) => `${baseUrl}/admin/roles/${id}`,
        DeleteRole: (id: string) => `${baseUrl}/admin/roles/${id}`,
        GetAllScreens: `${baseUrl}/admin/roles/screens`,
        GetRolePermissions: (id: string) => `${baseUrl}/admin/roles/${id}/permissions`,
        UpdateRolePermissions: (id: string) => `${baseUrl}/admin/roles/${id}/permissions`,
        AssignRole: `${baseUrl}/admin/roles/assign`,
        GetMyPermissions: `${baseUrl}/admin/roles/my-permissions`,
    },
    Users: {
        GetAllUsers: `${baseUrl}/admin/users`,
        GetUserById: (id: string) => `${baseUrl}/admin/users/${id}`,
        UpdateUser: (id: string) => `${baseUrl}/admin/users/${id}`,
        DeleteUser: (id: string) => `${baseUrl}/admin/users/${id}`,
    },
    AppHeader: {
        GetHeaderSettings: `${baseUrl}/customization/header`,
        UploadHeader: `${baseUrl}/customization/header/upload`,
        UpdateHeaderPosition: `${baseUrl}/customization/header/position`,
        DeleteHeader: `${baseUrl}/customization/header`,
        GetOverlay: `${baseUrl}/customization/header/overlay`,
        UpdateOverlay: `${baseUrl}/customization/header/overlay`,
    },
    SplashScreen: {
        GetSettings: `${baseUrl}/customization/splash`,
        UploadLogo: `${baseUrl}/customization/splash/upload`,
        UpdateSettings: `${baseUrl}/customization/splash`,
        DeleteLogo: `${baseUrl}/customization/splash/logo`,
    },
    ProductDetail: {
        GetSections: `${baseUrl}/customization/product-detail`,
        UpdateSections: `${baseUrl}/customization/product-detail`,
    },
    Analytics: {
        RegisterAnonymous: `${baseUrl}/analytics/register-anonymous`,
        TrackActivity: `${baseUrl}/analytics/track-activity`,
        LinkUser: `${baseUrl}/analytics/link-user`,
        GetSummary: (startDate?: string, endDate?: string) => {
            const params = new URLSearchParams();
            
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            return `${baseUrl}/analytics/summary${params.toString() ? '?' + params.toString() : ''}`;
        },
        GetActivities: (limit?: number, offset?: number) => {
            const params = new URLSearchParams();
            
            if (limit) params.append('limit', limit.toString());
            if (offset) params.append('offset', offset.toString());
            return `${baseUrl}/analytics/activities${params.toString() ? '?' + params.toString() : ''}`;
        },
        GetTimeline: (startDate?: string, endDate?: string, activityType?: string, anonymousId?: string, userTimezone?: string) => {
            const params = new URLSearchParams();
            
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            if (activityType) params.append('activityType', activityType);
            if (anonymousId) params.append('anonymousId', anonymousId);
            if (userTimezone) params.append('userTimezone', userTimezone);
            return `${baseUrl}/analytics/timeline${params.toString() ? '?' + params.toString() : ''}`;
        },
        GetTopProducts: (limit?: number, startDate?: string, endDate?: string) => {
            const params = new URLSearchParams();
            if (limit) params.append('limit', limit.toString());
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            return `${baseUrl}/analytics/top-products${params.toString() ? '?' + params.toString() : ''}`;
        },
        GetProductsWithViews: (startDate?: string, endDate?: string) => {
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            return `${baseUrl}/analytics/products-with-views${params.toString() ? '?' + params.toString() : ''}`;
        },
        GetLowStockProducts: `${baseUrl}/analytics/low-stock-products`,
        GetLowestStockProducts: (limit?: number) => {
            const params = new URLSearchParams();
            if (limit) params.append('limit', limit.toString());
            return `${baseUrl}/analytics/lowest-stock-products${params.toString() ? '?' + params.toString() : ''}`;
        },
        GetTopCartProducts: (limit?: number) => {
            const params = new URLSearchParams();
            if (limit) params.append('limit', limit.toString());
            return `${baseUrl}/analytics/top-cart-products${params.toString() ? '?' + params.toString() : ''}`;
        },
        GetSalesVsNonsales: (startDate?: string, endDate?: string) => {
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            return `${baseUrl}/analytics/sales-vs-nonsales${params.toString() ? '?' + params.toString() : ''}`;
        },
        GetCategoryPerformance: `${baseUrl}/analytics/category-performance`,
        GetProductViewsOverTime: (startDate?: string, endDate?: string, granularity?: string) => {
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            if (granularity) params.append('granularity', granularity);
            return `${baseUrl}/analytics/product-views-over-time${params.toString() ? '?' + params.toString() : ''}`;
        },
        GetProductSummaryStats: (startDate?: string, endDate?: string) => {
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            return `${baseUrl}/analytics/product-summary-stats${params.toString() ? '?' + params.toString() : ''}`;
        },
        GetStockLevelDistribution: `${baseUrl}/analytics/stock-level-distribution`,
        GetPriceRangeDistribution: `${baseUrl}/analytics/price-range-distribution`,
        GetProductActivitySummary: (startDate?: string, endDate?: string) => {
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            return `${baseUrl}/analytics/product-activity-summary${params.toString() ? '?' + params.toString() : ''}`;
        },
        GetViewsStockCorrelation: (startDate?: string, endDate?: string) => {
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            return `${baseUrl}/analytics/views-stock-correlation${params.toString() ? '?' + params.toString() : ''}`;
        },
        GetDiscountImpact: (startDate?: string, endDate?: string) => {
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            return `${baseUrl}/analytics/discount-impact${params.toString() ? '?' + params.toString() : ''}`;
        },
        GetProductFreshness: (daysThreshold?: number) => {
            const params = new URLSearchParams();
            if (daysThreshold) params.append('daysThreshold', daysThreshold.toString());
            return `${baseUrl}/analytics/product-freshness${params.toString() ? '?' + params.toString() : ''}`;
        },
        GetCategoryStockHealth: `${baseUrl}/analytics/category-stock-health`,
    },
    RestaurantSubmissions: {
        Create: `${baseUrl}/restaurant-submissions`,
        GetAll: `${baseUrl}/restaurant-submissions`,
        GetById: (id: string) => `${baseUrl}/restaurant-submissions/${id}`,
        Update: (id: string) => `${baseUrl}/restaurant-submissions/${id}`,
        Delete: (id: string) => `${baseUrl}/restaurant-submissions/${id}`,
    },
};

// Stock Level Thresholds
export const STOCK_LEVELS = {
    LOW_THRESHOLD: 10,      // Red (0-10): Critical, needs immediate attention
    MEDIUM_THRESHOLD: 30,   // Orange/Yellow (11-30): Warning, plan restocking
    // Above 30: Green, healthy stock levels
};
